/**
 * LES CALCULS DU BILAN COMPTE-GOUTTES, hors de tout rendu.
 *
 * Un bilan de fin de séance est une suite de lectures d'un même état : ce que
 * la salle a adopté, qui l'a payé, où sont passées les économies. Les mettre
 * ici plutôt que dans le JSX permet de les VÉRIFIER (acgSummary.test.js) — un
 * chiffre projeté en fin d'atelier et commenté devant trente personnes ne peut
 * pas reposer sur une expression enfouie dans un `map`.
 *
 * TOUT EST DÉFENSIF, à dessein. Ces fonctions tournent sur des parties réelles,
 * dont certaines ont commencé avant l'existence du journal des mesures, et sur
 * des ateliers dont les auteurs n'ont pas tous renseigné les mêmes champs. Une
 * lecture impossible rend une absence explicite (`null`, liste vide) que
 * l'écran sait masquer — jamais un `NaN` affiché en gros.
 */

/** Les libellés des trois axes portés par `decision.metadata`. */
export const CATEGORY_LABELS = {
  contraindre: 'Contraindre',
  inciter: 'Inciter',
  autre: 'Autre',
  // Une mesure non classée se voit (breakdownBy garde les clés inconnues) ;
  // autant qu'elle porte un nom, l'écran est projeté.
  inconnu: 'Non renseigné',
};

export const SECTOR_LABELS = {
  agriculture: 'Agriculture',
  industry: 'Industrie',
  public: 'Secteur public',
  consumer: 'Particuliers',
};

/**
 * LA COULEUR D'UN SECTEUR, DÉFINIE UNE FOIS.
 *
 * L'histogramme de projection (WaterHistogramChart) et les barres du bilan
 * parlent des mêmes secteurs à quelques centimètres l'un de l'autre : deux
 * tables de couleurs qui divergeraient feraient dire à l'écran deux choses
 * contradictoires. Ce sont les teintes historiques du graphe, remontées ici et
 * importées par lui.
 */
export const SECTOR_COLORS = {
  agriculture: '#ff9f40',
  industry: '#36a2eb',
  public: '#ffcd56',
  consumer: '#4bc0c0',
};

/** Le gris des secteurs et des catégories qu'aucune table ne nomme. */
export const NEUTRAL_COLOR = '#a7b8c2';

/** Contraindre est la couleur de la friction, inciter celle de l'eau. */
export const CATEGORY_COLORS = {
  contraindre: '#db6c52',
  inciter: '#3587a1',
  autre: '#b7c6cf',
};

// Doit rester d'accord avec DecisionCategoryChart.js, qui porte la même table
// pour l'écran Disney.
export const LEVER_LABELS = {
  sensibilisation_responsabilisation: 'Sensibilisation & responsabilisation',
  sobriete_hydrique: 'Sobriété hydrique',
  revalorisation_d_eaux_non_conventionnelles: "Revalorisation d'eaux non conventionnelles",
  solution_fondee_sur_la_nature: 'Solution fondée sur la nature',
  innovation: 'Innovation',
  autre: 'Autre',
  inconnu: 'Inconnu',
};

/** Acteurs jamais mis au podium : ils arbitrent, ils ne concourent pas. */
export const NON_COMPETING_ACTORS = ['agence-eau'];

/**
 * LE SCORE QUI SERT DE BUDGET, résolu contre la partie et non lu tel quel.
 *
 * `game_status.decision_metric_id` n'est PAS fiable : GameConfigurator ne
 * l'envoie pas à /init_game, qui retombe donc toujours sur 'money'
 * (app.py:47). Les ateliers ACG dont le budget est 'vote' n'ont un écran qui
 * tient que parce qu'ils traînent un score 'money' résiduel. On préfère donc
 * la valeur déclarée quand elle existe vraiment dans la partie, puis 'vote'.
 */
export function budgetScoreId(gameStatus) {
  const scores = gameStatus?.score_dic || {};
  const declared = gameStatus?.decision_metric_id;
  if (declared && declared in scores) return declared;
  if ('vote' in scores) return 'vote';
  if ('money' in scores) return 'money';
  return null;
}

/**
 * LES MESURES ADOPTÉES, sous une forme unique.
 *
 * Deux sources possibles, et la différence compte : `decision_log`
 * (game_functions._log_decision) porte l'ordre, les titres, le coût payé et
 * les positions des acteurs ; `choosen_decisions` ne porte que les métadonnées,
 * et c'est tout ce dont disposent les parties commencées avant le journal.
 * `hasLog` dit laquelle on a, pour que l'écran masque ce qu'il ne peut pas dire.
 */
export function adoptedMeasures(gameStatus) {
  const log = gameStatus?.decision_log;
  if (Array.isArray(log) && log.length) {
    return {
      hasLog: true,
      measures: log.map((entry) => ({
        id: entry.decision_id,
        title: entry.title || entry.decision_id,
        round: entry.round ?? null,
        cost: Number(entry.cost) || 0,
        metadata: entry.metadata || {},
        positions: entry.positions || {},
      })),
    };
  }
  const chosen = gameStatus?.choosen_decisions || {};
  return {
    hasLog: false,
    measures: Object.entries(chosen).map(([id, metadata]) => ({
      id,
      title: id,
      round: null,
      cost: 0,
      metadata: metadata || {},
      positions: {},
    })),
  };
}

/**
 * Répartition des mesures selon un axe de `metadata`, du plus fréquent au moins
 * fréquent. `labels` nomme les clés ; une clé inconnue garde son identifiant
 * plutôt que de disparaître — une mesure mal classée doit se voir.
 */
export function breakdownBy(measures, field, labels = {}) {
  const counts = new Map();
  measures.forEach((measure) => {
    const key = measure.metadata?.[field] || 'inconnu';
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  const total = measures.length || 1;
  return [...counts.entries()]
    .map(([key, count]) => ({
      key,
      label: labels[key] || key,
      count,
      share: count / total,
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * LES ÉCONOMIES À L'HORIZON DE LA PROJECTION, par secteur.
 *
 * `water_projection.metadata` est un histogramme par secteur, une case par
 * tour, dont les valeurs sont reportées vers l'avant : la DERNIÈRE case porte
 * donc l'économie de long terme, pas celle d'un tour. C'est elle qu'on lit.
 */
export function horizonSavingsBySector(gameStatus, scoreId = 'water_projection') {
  const metadata = (gameStatus?.score_dic || {})[scoreId]?.metadata;
  if (!metadata || typeof metadata !== 'object') return [];
  return Object.entries(metadata)
    .filter(([, series]) => Array.isArray(series) && series.length)
    .map(([sector, series]) => ({
      key: sector,
      label: SECTOR_LABELS[sector] || sector,
      value: Number(series[series.length - 1]) || 0,
    }));
}

/**
 * LES ÉCONOMIES PAR SECTEUR, EN VALEUR ET EN PART, du plus gros au plus petit.
 *
 * La part est celle du TOTAL projeté : c'est elle qui répond à la question du
 * débrief — d'où vient l'eau économisée pendant la séance. Un total nul (aucune
 * mesure adoptée) rend des parts à zéro plutôt qu'une division par zéro : les
 * secteurs restent listés, leurs barres sont vides.
 */
export function sectorSavings(gameStatus) {
  const rows = horizonSavingsBySector(gameStatus);
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  return rows
    .map((row) => ({ ...row, share: total ? row.value / total : 0 }))
    .sort((a, b) => b.value - a.value);
}

/**
 * LE REGISTRE DES ACTEURS : satisfaction finale, et ce que chacun a obtenu ou
 * cédé.
 *
 * `conceded` compte les mesures ADOPTÉES contre lesquelles l'acteur s'était
 * prononcé — c'est la définition de l'effort qui se tient : la satisfaction
 * finale mélange les concessions avec les impacts subis sans que l'acteur ait
 * eu voix au chapitre.
 */
export function actorLedger(gameStatus, measures) {
  const scores = gameStatus?.score_dic || {};
  const selected = gameStatus?.list_selected_actors || [];
  return selected
    .filter((actorId) => scores[actorId])
    .map((actorId) => {
      let won = 0;
      let conceded = 0;
      measures.forEach((measure) => {
        const position = measure.positions?.[actorId];
        if (position === 'positive') won += 1;
        if (position === 'negative') conceded += 1;
      });
      return {
        id: actorId,
        name: scores[actorId].name || actorId,
        icon: scores[actorId].icon || '',
        value: Number(scores[actorId].value) || 0,
        initial: Number(gameStatus?.past_score?.init?.[actorId]) || null,
        won,
        conceded,
      };
    });
}

/** Les trois premiers par satisfaction, arbitres exclus. Égalité = même rang. */
export function podium(ledger) {
  const ranked = ledger
    .filter((actor) => !NON_COMPETING_ACTORS.includes(actor.id))
    .slice()
    .sort((a, b) => b.value - a.value)
    .slice(0, 3);
  let rank = 0;
  return ranked.map((actor, index) => {
    if (index === 0 || actor.value !== ranked[index - 1].value) rank = index + 1;
    return { ...actor, rank };
  });
}

/**
 * L'ACTEUR QUI A FAIT LE PLUS D'EFFORT : celui qui a laissé passer le plus de
 * mesures qu'il combattait. Sans journal, on retombe sur la satisfaction la
 * plus basse — la mesure de l'ancien bilan, moins juste mais toujours lisible.
 */
export function biggestEffort(ledger, hasLog) {
  const candidates = ledger.filter((actor) => !NON_COMPETING_ACTORS.includes(actor.id));
  if (!candidates.length) return null;
  if (hasLog && candidates.some((actor) => actor.conceded > 0)) {
    return candidates.slice().sort((a, b) => b.conceded - a.conceded)[0];
  }
  return candidates.slice().sort((a, b) => a.value - b.value)[0];
}

/**
 * LES MESURES QUI ONT CLIVÉ, ET CELLES QUI ONT FAIT CONSENSUS — le matériau du
 * débrief. Une mesure clive d'autant plus qu'elle a des partisans ET des
 * opposants ; elle fait consensus quand personne ne s'y est opposé.
 */
export function measureConsensus(measures) {
  const scored = measures
    .map((measure) => {
      const positions = Object.values(measure.positions || {});
      const forCount = positions.filter((p) => p === 'positive').length;
      const againstCount = positions.filter((p) => p === 'negative').length;
      return {
        id: measure.id,
        title: measure.title,
        forCount,
        againstCount,
        tension: Math.min(forCount, againstCount),
      };
    })
    .filter((row) => row.forCount + row.againstCount > 0);

  const divisive = scored
    .filter((row) => row.tension > 0)
    .sort((a, b) => b.tension - a.tension || b.againstCount - a.againstCount)
    .slice(0, 3);
  const consensual = scored
    .filter((row) => row.againstCount === 0 && row.forCount > 0)
    .sort((a, b) => b.forCount - a.forCount)
    .slice(0, 3);
  return { divisive, consensual };
}

/**
 * L'AVANCEMENT VERS L'OBJECTIF.
 *
 * `targetPerRound` vient de `content.metadata.target_per_round` : c'est le
 * nombre de points d'empreinte qu'un tour bien joué rapporte. La valeur par
 * défaut, 20, est celle qu'DisneyEndPage.js écrivait en dur — reprise ici pour
 * ne pas changer les chiffres au passage, mais RÉGLABLE par la page, ce qu'elle
 * n'était pas.
 *
 * Rend `null` quand l'atelier ne déclare pas son nombre de tours, plutôt qu'un
 * pourcentage divisé par zéro.
 */
export function objectiveProgress(gameStatus, targetPerRound = 20, scoreId = 'water_projection') {
  const value = (gameStatus?.score_dic || {})[scoreId]?.value;
  const rounds = Number(gameStatus?.nb_rounds_cg) || 0;
  if (value == null || !rounds || !targetPerRound) return null;
  const target = rounds * targetPerRound;
  return { value, target, share: value / target, percent: Math.round((100 * value) / target) };
}
